import { BackupService } from '@qdrant/qdrant-cloud-public-api/cluster/backup/v1';
import {
  listBackups,
  listBackupSchedules,
  getBackupSchedule,
  listBackupRestores,
  createBackupSchedule,
  updateBackupSchedule,
  deleteBackupSchedule,
  createBackup,
  deleteBackup,
  restoreBackup,
  getBackup,
  updateBackup,
} from '@qdrant/qdrant-cloud-public-api/cluster/backup/v1/connect-query';
import { createClusterFromBackup, listClusters } from '@qdrant/qdrant-cloud-public-api/cluster/v1/connect-query';
import { type QueryClient, useMutation as useQueryMutation } from '@tanstack/react-query';
import { type AnyRouter, useRouter } from '@tanstack/react-router';
import { transport } from '@/api/transport';
import { createConnectQueryKey, createServiceQueries, type MutationFactory, queryOptions } from '@/connect-query';
import { useMutation } from '@/connect-query/hooks/useMutation';
import { deleteBackupsPool } from './delete-backups-pool';

// The backup list polls faster while a backup is pending, running or being deleted, so its end shows soon.
export const BACKUP_BUSY_POLL_MS = 5_000;
export const BACKUP_IDLE_POLL_MS = 15_000;

export const backupService = createServiceQueries(BackupService, {
  getBackupQuery: (params) => queryOptions(getBackup, params, { transport }),
  getBackupScheduleQuery: (params) => queryOptions(getBackupSchedule, params, { transport }),
  listBackupRestoresQuery: (params) => queryOptions(listBackupRestores, params, { transport }),
  listBackupSchedulesQuery: (params) => queryOptions(listBackupSchedules, params, { transport }),
  listBackupsQuery: (params) => queryOptions(listBackups, params, { transport }),
});

// gRPC Mutations using MutationFactory pattern
export const useCreateClusterBackupScheduleMutation: MutationFactory<typeof createBackupSchedule> = () =>
  useMutation(createBackupSchedule, {
    onSuccess: (_data, variables, { router }, { client }) =>
      Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listBackupSchedules,
            input: { accountId: variables.backupSchedule?.accountId },
          }),
        }),
        router.invalidate(),
      ]),
  });

export const useUpdateClusterBackupScheduleMutation: MutationFactory<typeof updateBackupSchedule> = () =>
  useMutation(updateBackupSchedule, {
    onSuccess: (_data, variables, { router }, { client }) =>
      Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listBackupSchedules,
            input: { accountId: variables.backupSchedule?.accountId },
          }),
        }),
        router.invalidate(),
      ]),
  });

export const useDeleteClusterBackupScheduleMutation: MutationFactory<typeof deleteBackupSchedule> = () =>
  useMutation(deleteBackupSchedule, {
    onSuccess: (_data, { accountId, deleteBackups }, { router }, { client }) =>
      Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listBackupSchedules,
            input: { accountId },
          }),
        }),
        ...(deleteBackups
          ? [
              client.invalidateQueries({
                queryKey: createConnectQueryKey({
                  schema: listBackups,
                  input: { accountId },
                }),
              }),
            ]
          : []),
        router.invalidate(),
      ]),
  });

export const useRestoreClusterBackupMutation: MutationFactory<typeof restoreBackup> = () =>
  useMutation(restoreBackup, {
    onSuccess: (_data, { accountId }, { router }, { client }) =>
      Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listBackups,
            input: { accountId },
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listBackupRestores,
            input: { accountId },
          }),
        }),
        router.invalidate(),
      ]),
  });

export const useCreateInstantBackupMutation: MutationFactory<typeof createBackup> = () =>
  useMutation(createBackup, {
    onSuccess: async (_data, { backup }, { router }, { client }) =>
      Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listBackups,
            input: { accountId: backup.accountId },
          }),
        }),
        router.invalidate(),
      ]),
  });

export const useUpdateBackupMutation: MutationFactory<typeof updateBackup> = () =>
  useMutation(updateBackup, {
    onSuccess: (_data, { backup }, { router }, { client }) =>
      Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listBackups,
            input: { accountId: backup.accountId },
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: getBackup,
            input: { accountId: backup.accountId, backupId: backup.id },
          }),
        }),
        router.invalidate(),
      ]),
  });

const invalidateDeletedBackups = (client: QueryClient, router: AnyRouter, accountId: string) =>
  Promise.all([
    client.invalidateQueries({
      queryKey: createConnectQueryKey({
        schema: listBackups,
        input: { accountId },
      }),
    }),
    client.invalidateQueries({
      queryKey: createConnectQueryKey({
        schema: getBackup,
        input: { accountId },
      }),
    }),
    client.invalidateQueries({
      queryKey: createConnectQueryKey({
        schema: listBackupRestores,
        input: { accountId },
      }),
    }),
    router.invalidate(),
  ]);

export const useDeleteClusterBackupMutation: MutationFactory<typeof deleteBackup> = () =>
  useMutation(deleteBackup, {
    onSuccess: (_data, { accountId }, { router }, { client }) => invalidateDeletedBackups(client, router, accountId),
  });

/**
 * Deletes many backups through the per-backup rpc, a few at a time, and invalidates once when all settled.
 * Resolves with the ids that succeeded and failed; one failure never rejects it.
 */
export const useDeleteClusterBackupsMutation = () => {
  const router = useRouter();
  const { mutateAsync: deleteOne } = useMutation(deleteBackup);

  return useQueryMutation({
    mutationFn: ({ accountId, backupIds }: { accountId: string; backupIds: readonly string[] }) =>
      deleteBackupsPool(backupIds, (backupId) => deleteOne({ accountId, backupId })),
    onSettled: (_data, _error, { accountId }, _onMutateResult, { client }) =>
      invalidateDeletedBackups(client, router, accountId),
  });
};

export const useCreateClusterFromBackupMutation: MutationFactory<typeof createClusterFromBackup> = () =>
  useMutation(createClusterFromBackup, {
    onSuccess: (_data, { accountId }, { router }, { client }) =>
      Promise.all([
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listBackups,
            input: { accountId },
          }),
        }),
        client.invalidateQueries({
          queryKey: createConnectQueryKey({
            schema: listClusters,
            input: { accountId },
          }),
        }),
        router.invalidate(),
      ]),
  });
